import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";

import { OfferPageChrome } from "~/components/offer-page/offer-page-chrome";
import { Button } from "~/components/ui/button";
import { CoachLeadForm } from "~/components/wheel/coach-lead-form";
import {
  CoachWheelHeader,
  CoachWheelIntro,
} from "~/components/wheel/coach-page";
import { SequentialRater } from "~/components/wheel/sequential-rater";
import { Wheel } from "~/components/wheel/wheel";
import { trackWheelCompleted } from "~/lib/monitoring/track-event";
import {
  coachLeadLinkMessage,
  type CoachLeadLinkProblem,
} from "~/lib/shared/coach-leads";
import { sameWheelLabels } from "~/lib/shared/coach-wheel";
import { createBrandMeta } from "~/lib/shared/meta";
import { WHEEL_CATEGORY_COUNT } from "~/lib/shared/wheel";

const REVEAL_AFTER_TOUCHES = 4;

interface CoachWheelSearch {
  from?: string;
  /** A "Save my results" link from the results email did not work. */
  link?: CoachLeadLinkProblem;
}

/**
 * A coach's own Wheel of Life page. Visitors see it while the coach's page is
 * on and their profile is public; everyone else goes to `/wheel-of-life`
 * (302, because the page can come back). The signed-in owner always sees it,
 * as a preview. Its canonical names the platform page, which gets the search
 * credit, and it never carries a robots tag, even for a coach who turned
 * search indexing off — so it sits outside the `_profile` layout.
 */
export const Route = createFileRoute("/$username/wheel-of-life/")({
  validateSearch: (search: Record<string, unknown>): CoachWheelSearch => ({
    ...(typeof search.from === "string" ? { from: search.from } : {}),
    ...(search.link === "expired" || search.link === "invalid"
      ? { link: search.link }
      : {}),
  }),
  loaderDeps: ({ search }) => ({ from: search.from, link: search.link }),
  loader: async ({ params, deps }) => {
    const { getCoachWheelPageFn, getWheelSessionFn } =
      await import("~/lib/functions/wheel");
    const page = await getCoachWheelPageFn({
      data: { username: params.username },
    });
    if (!page) {
      throw redirect({
        to: "/wheel-of-life",
        search: deps.link ? { link: deps.link } : {},
        statusCode: 302,
      });
    }

    // Re-score keeps the earlier scores only while the categories are the
    // ones they were scored on; after the coach renamed them it starts fresh.
    let previousScores: number[] | null = null;
    if (deps.from) {
      const previous = await getWheelSessionFn({ data: { id: deps.from } });
      if (previous && sameWheelLabels(previous.labels, page.labels)) {
        previousScores = previous.scores;
      }
    }
    return { page, previousScores };
  },
  staticData: { title: "Wheel of Life" },
  head: ({ loaderData }) => {
    const name = loaderData?.page.coach.fullName;
    const title = name
      ? `Wheel of Life with ${name}`
      : "Wheel of Life Assessment";
    const description = name
      ? `Score eight areas of your life in a few minutes and see where you are thriving and where you feel stretched. Then talk it through with ${name}.`
      : "Score eight areas of your life in a few minutes.";
    return createBrandMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      ogImage: "/og/wheel-of-life-generic.png",
      ogType: "website",
      twitterCard: "summary_large_image",
      canonical: "https://dreamcoachmatch.com/wheel-of-life",
    });
  },
  component: CoachWheelPage,
});

function CoachWheelPage(): React.JSX.Element {
  const { page, previousScores } = Route.useLoaderData();
  const { link } = Route.useSearch();
  const navigate = useNavigate();
  // Each start is a new consent, so the form comes first on every visit.
  const [started, setStarted] = useState(false);
  // Local state only: the platform page's stored wheel (and any areas a
  // visitor renamed there) must never leak into a coach's categories.
  const [scores, setScores] = useState<number[]>(
    () => previousScores ?? Array(WHEEL_CATEGORY_COUNT).fill(5),
  );
  const [touchedMask, setTouchedMask] = useState<boolean[]>(() =>
    Array(WHEEL_CATEGORY_COUNT).fill(previousScores !== null),
  );
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const touchedCount = useMemo(
    () => touchedMask.filter(Boolean).length,
    [touchedMask],
  );
  const ctaReady = touchedCount >= REVEAL_AFTER_TOUCHES;
  const remaining = Math.max(0, REVEAL_AFTER_TOUCHES - touchedCount);

  const handleScore = useCallback((index: number, score: number) => {
    const clamped = Math.max(1, Math.min(10, Math.round(score)));
    setScores((prev) => prev.map((s, i) => (i === index ? clamped : s)));
    setTouchedMask((prev) => prev.map((t, i) => (i === index ? true : t)));
  }, []);

  const handleAdvance = useCallback(() => {
    setCurrentIdx((prev) => {
      const len = touchedMask.length;
      for (let step = 1; step <= len; step++) {
        const candidate = (prev + step) % len;
        if (!touchedMask[candidate]) return candidate;
      }
      return Math.min(len - 1, prev + 1);
    });
  }, [touchedMask]);

  const handleBack = useCallback(() => {
    setCurrentIdx((prev) => Math.max(0, prev - 1));
  }, []);

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const { createWheelSessionFn } = await import("~/lib/functions/wheel");
      const { id, coachUsername, leadCompleted } = await createWheelSessionFn({
        data: {
          labels: page.labels,
          scores,
          coachUsername: page.coach.username,
        },
      });
      // The page can go off while a visitor scores; the session is then a
      // platform one and its results live on the platform page.
      trackWheelCompleted(coachUsername ? "coach" : "public");
      if (coachUsername) {
        await navigate({
          to: "/$username/wheel-of-life/results/$id",
          params: { username: coachUsername, id },
          search: leadCompleted ? { emailed: true } : {},
        });
      } else {
        await navigate({
          to: "/wheel-of-life/results/$id",
          params: { id },
          search: { claim: undefined },
        });
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <OfferPageChrome>
      <div className="mx-auto max-w-4xl px-4 pt-8 pb-16 sm:pt-10">
        <CoachWheelHeader coach={page.coach} isLive={page.isLive} />

        {link && (
          <p
            role="status"
            className="mx-auto mt-6 max-w-xl rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200"
          >
            {coachLeadLinkMessage(link, page.coach.firstName)}
          </p>
        )}

        <header className="mx-auto mt-8 max-w-xl">
          <h1 className="text-3xl leading-[1.15] font-medium tracking-[-0.015em] text-neutral-900 sm:text-4xl">
            How balanced is your life right now?
          </h1>
          <CoachWheelIntro intro={page.copy?.intro ?? null} />
        </header>

        {started ? (
          <>
            <section className="mt-6 flex justify-center">
              <div className="w-full max-w-xl rounded-2xl bg-white px-4 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_-12px_rgba(0,0,0,0.06)] ring-1 ring-neutral-200/70 sm:px-6 sm:py-6">
                <SequentialRater
                  labels={page.labels}
                  scores={scores}
                  touchedMask={touchedMask}
                  currentIdx={currentIdx}
                  onScore={handleScore}
                  onAdvance={handleAdvance}
                  onBack={handleBack}
                  onComplete={handleSubmit}
                  submitting={submitting}
                />
              </div>
            </section>

            <section className="mt-6 flex justify-center">
              <Wheel
                mode="interactive"
                labels={page.labels}
                scores={scores}
                touchedMask={touchedMask}
                spotlightIndex={currentIdx}
                onScoreChange={handleScore}
                onWedgeFocus={setCurrentIdx}
              />
            </section>

            <div className="mt-6 flex flex-col items-center text-center">
              {ctaReady ? (
                <Button
                  variant="primary"
                  size="lg"
                  className="rounded-full px-8"
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? "Reading your wheel…" : "See my results"}
                </Button>
              ) : (
                <p className="text-sm text-neutral-500">
                  Score{" "}
                  <span className="font-medium text-neutral-700">
                    {remaining} more area{remaining === 1 ? "" : "s"}
                  </span>{" "}
                  to see your results.
                </p>
              )}
            </div>
          </>
        ) : (
          <section className="mt-6 flex justify-center">
            <div className="w-full max-w-xl rounded-2xl bg-white px-4 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_8px_24px_-12px_rgba(0,0,0,0.06)] ring-1 ring-neutral-200/70 sm:px-6 sm:py-6">
              <CoachLeadForm
                coach={page.coach}
                isLive={page.isLive}
                onStarted={() => setStarted(true)}
              />
            </div>
          </section>
        )}
      </div>
    </OfferPageChrome>
  );
}
